import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Icon, Stack, SxProps, Tab, Tabs, Tooltip, Typography } from '@mui/material';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import AlertDialog from '../AlertDialog';
import ProcessingView from '../ProcessingView';
import { cancelProcessing, getProcessingComplete } from '../../store/processing/processingSlice';
import { PrometheusTab } from '../../types/generic';
import { cancelFileUpload } from '../../store/upload/uploadSlice';
import { clearQueuedTab, getTabPaths, getTabs, goBack, queueTab, switchTab } from '../../store/tab/tabSlice';
import { ArrowBack, Build } from '@mui/icons-material';
import { pathToTab } from '../Tabs';
import { callAPISync, getDisabledTabs } from '../../utils/api';
import { useLogto } from '@logto/react';
import Logger from '../../utils/logger';

const parentTabStyle: SxProps = { alignItems: 'center', marginBottom: 2, border: 1, borderRight: 0, marginTop: 0, padding: 0 };

const TabSelector: React.FC = () => {
	const dispatch = useAppDispatch();
	const logto = useLogto();

	const uploadStatus = useAppSelector((state) => state.upload.status);
	const uploadType = useAppSelector((state) => state.upload.uploadType);
	const warningMessage = useAppSelector((state) => state.upload.warningMessage);

	const processingFile = useAppSelector((state) => state.processing.fileId);
	const processingFinished = useAppSelector((state) => getProcessingComplete(state));

	const possibleTabs = useAppSelector((state) => getTabs(state));
	const tabPaths = useAppSelector((state) => getTabPaths(state));
	const currentTab = useAppSelector((state) => state.tab.currentTab);
	const queuedTab = useAppSelector((state) => state.tab.queuedTab);
	const previousTabs = useAppSelector((state) => state.tab.previousTabs);
	const [disabledTabs, setDisabledTabs] = useState<string[]>([]);

	const currentlyProcessing = Boolean(processingFile) && !processingFinished;

	useEffect(() => {
		callAPISync(logto, (token) => {
			getDisabledTabs(token)
				.then((res) => {
					if (res.status === 204) {
						return [];
					} else if (res.status === 200) {
						return res.json();
					} else {
						throw new Error(`${res.status} ${res.statusText}`);
					}
				})
				.then((resp: string[]) => {
					setDisabledTabs(resp);
				})
				.catch((e) => {
					Logger.error(e);
				});
		});
	}, [currentTab]);

	const updateTab = (newTabName: string) => {
		if (uploadStatus && warningMessage) {
			dispatch(queueTab(newTabName));
		} else {
			dispatch(switchTab(newTabName));

			if (processingFinished) {
				dispatch(cancelProcessing());
			}
		}
	};

	const handleChange = (newValue: string) => {
		const tab = pathToTab(newValue);

		if (tab.children) {
			let tabChild: PrometheusTab | undefined = undefined;
			if (tab.defaultChild) {
				tabChild = tab.children[tab.defaultChild];
			} else {
				tabChild = tab.children[0];
			}

			// getTabs means that this will always be defined, even if typescript doesn't think it will
			if (tabChild) {
				const newTabName = `${tab.name}.${tabChild.name}`;
				updateTab(newTabName);
			}
		} else {
			updateTab(newValue);
		}
	};

	return (
		<>
			<Stack
				sx={{
					display: 'flex',
					justifyContent: 'top',
					alignItems: 'center',
					width: '100vw',
					boxSizing: 'border-box',
					paddingY: 2,
				}}
			>
				<Stack
					direction="row"
					sx={{
						width: '80%',
					}}
				>
					<Tabs
						orientation="vertical"
						value={tabPaths[0]}
						onChange={(_, val) => handleChange(val)}
						sx={{
							minWidth: '200px',
							paddingY: 2,
							paddingX: 0,
						}}
					>
						{possibleTabs.map((tab) => (
							<Tab
								key={tab.name}
								value={tab.name}
								data-testid={`${tab.name.toLocaleLowerCase()}-tab-button`}
								label={
									<Tooltip title={tab.helpText} key={tab.name} placement="right">
										<Stack direction="column" alignItems="center" width={'100%'} margin={1.5}>
											<Icon component={tab.icon} />
											<Typography>{tab.name}</Typography>
										</Stack>
									</Tooltip>
								}
								sx={parentTabStyle}
								disabled={currentlyProcessing}
							/>
						))}
					</Tabs>

					<Stack flex={1} justifyContent="center" alignItems="left" sx={{ p: 2, border: 1, borderRadius: 2 }}>
						{processingFile ? (
							<ProcessingView />
						) : (
							<Stack spacing={2} alignItems={'center'} height={'100%'}>
								<Stack direction={'row'} width={'100%'} justifyContent={'center'}>
									<Button
										size="small"
										aria-label="Click to navigate to the previous tab"
										disabled={previousTabs.length === 0}
										variant="text"
										onClick={() => dispatch(goBack())}
										startIcon={<ArrowBack />}
										sx={{ marginBottom: 1, width: '50px', textDecoration: 'underline' }}
									>
										Back
									</Button>

									<Box flex={1} display={'flex'} justifyContent={'center'}>
										{tabPaths.length > 1 && (
											<Tabs value={tabPaths[1]} onChange={(_, newValue) => updateTab(newValue)} variant="scrollable">
												{possibleTabs
													.find((tab) => tab.name === tabPaths[0])
													?.children?.map((childTab) => {
														const tabPath = `${tabPaths[0]}.${childTab.name}`;
														return (
															<Tab
																key={tabPath}
																value={tabPath}
																data-testid={`${tabPath.toLocaleLowerCase()}-tab-button`}
																sx={{ minWidth: '200px', paddingX: 1, paddingY: 0 }}
																label={
																	<Tooltip title={childTab.helpText} key={childTab.name}>
																		<Stack direction="column" alignItems="center" width={'100%'} margin={1.5} fontSize={'large'}>
																			<Icon component={childTab.icon} />
																			<>{childTab.name}</>
																		</Stack>
																	</Tooltip>
																}
																disabled={currentlyProcessing}
															/>
														);
													})}
											</Tabs>
										)}
									</Box>
								</Stack>
								{disabledTabs.includes(currentTab as string) ||
								disabledTabs.includes((currentTab as string).split('.')[0]) ||
								(disabledTabs.includes('All') && currentTab !== 'Admin.System') ? (
									<Alert icon={<Build />} severity="warning">
										Sorry, this feature is currently unavailable due to ongoing maintenance
									</Alert>
								) : (
									pathToTab(currentTab as string).component
								)}
							</Stack>
						)}
					</Stack>
				</Stack>
			</Stack>
			<AlertDialog
				title={`Cancel ${uploadType}?`}
				message={[`${warningMessage}, Are you sure you want to navigate away?`]}
				isOpen={Boolean(queuedTab) && uploadType !== null}
				leftButtonMessage="No"
				leftButtonAction={() => dispatch(clearQueuedTab())}
				rightButtonMessage="Yes"
				rightButtonAction={() => {
					if (queuedTab) {
						dispatch(switchTab(queuedTab));
						dispatch(cancelFileUpload());
						dispatch(clearQueuedTab());
					}
				}}
			/>
		</>
	);
};

export default TabSelector;
